// SPARES-PROC-SEP-001 Task 6 — Spare Master (read-only browse for store keepers).
//
// Store keepers need to look up spare code / name / drawing no / current
// stock without touching the create/edit/import controls reserved for the
// spares admin screen. This page is deliberately READ ONLY.

import { useState } from 'react'
import { Card, Input, Table, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'

import { sparesApi } from '@/features/maintenance/api'
import type { SparePart } from '@/shared/types/maintenance'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

export default function StoresSpareMasterPage() {
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['stores', 'spare-catalog', search],
    // 2000 = backend cap; the master has ~1,600 spares, so an unsearched
    // browse must not silently truncate the catalog.
    queryFn: () => sparesApi.list({ search: search || undefined, limit: 2000 }),
  })

  const columns = [
    { title: 'Spare Code', dataIndex: 'spare_code', key: 'spare_code' },
    { title: 'Name', dataIndex: 'spare_name', key: 'spare_name' },
    {
      title: 'Drawing No', dataIndex: 'drawing_no', key: 'drawing_no',
      render: (v: string | null | undefined) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Category', dataIndex: 'category', key: 'category',
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    { title: 'UOM', dataIndex: 'uom', key: 'uom' },
    {
      title: 'Current Stock', dataIndex: 'current_stock', key: 'current_stock',
      align: 'right' as const,
      render: (v: string | number) => Number(v ?? 0).toLocaleString('en-IN'),
    },
    {
      title: 'Avg Rate ₹', dataIndex: 'average_rate', key: 'average_rate',
      align: 'right' as const,
      render: (v: string | number) =>
        Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
  ]

  return (
    <div>
      <PageHeader title="Spare Master" subtitle="Browse the spare parts catalog — read only" />
      <Card>
        <Input.Search
          placeholder="Search code / name / drawing no"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => setSearch(v)}
          style={{ maxWidth: 420, marginBottom: 16 }}
        />
        <Table<SparePart>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  )
}
